'use client';

import React, { useEffect, useState, useMemo } from 'react';

interface OrderDetails {
  packageLabel: string;
  packageHours: number;
  packagePrice: number;
  customerName: string;
  customerEmail: string;
}

interface OrderConfirmationPageProps {
  orderDetails: OrderDetails | null;
  onNavigate: (page: string) => void;
}

const OrderConfirmationPage: React.FC<OrderConfirmationPageProps> = ({ orderDetails, onNavigate }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  const pkg = orderDetails ?? {
    packageLabel: 'Tutoring Package',
    packageHours: 0,
    packagePrice: 0,
    customerName: '',
    customerEmail: '',
  };

  const orderNumber = React.useMemo(() => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let result = 'SS-';
    for (let i = 0; i < 6; i++) result += chars[Math.floor(Math.random() * chars.length)];
    return result;
  }, []);

  const steps = [
    { icon: '📅', title: 'We\'ll reach out', desc: 'Our team will contact you within 24 hours to schedule your first session.' },
    { icon: '📚', title: 'Start learning', desc: 'Your tutor will tailor every session to your goals and learning style.' },
  ];

  return (
    <section
      style={{
        minHeight: '100vh',
        background: '#0a1628',
        padding: '5rem 1.5rem 6rem',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      {/* Animated checkmark circle */}
      <div
        style={{
          opacity: visible ? 1 : 0,
          transform: visible ? 'scale(1)' : 'scale(0.7)',
          transition: 'opacity 0.5s cubic-bezier(0.34,1.56,0.64,1), transform 0.5s cubic-bezier(0.34,1.56,0.64,1)',
          marginBottom: '2rem',
        }}
      >
        <div
          style={{
            width: 88,
            height: 88,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, rgba(244,196,48,0.25) 0%, rgba(244,196,48,0.08) 100%)',
            border: '2px solid rgba(244,196,48,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '2.4rem',
          }}
        >
          ✓
        </div>
      </div>

      {/* Heading */}
      <div
        style={{
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0)' : 'translateY(20px)',
          transition: 'opacity 0.5s ease 0.15s, transform 0.5s ease 0.15s',
          textAlign: 'center',
          marginBottom: '2.5rem',
        }}
      >
        <h1
          style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 'clamp(1.9rem, 5vw, 2.8rem)',
            fontWeight: 700,
            color: '#f4c430',
            margin: '0 0 0.6rem',
          }}
        >
          Order Confirmed!
        </h1>
        <p
          style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: '1rem',
            color: 'rgba(255,255,255,0.6)',
            margin: 0,
            lineHeight: 1.6,
          }}
        >
          {pkg.customerName ? `Thank you, ${pkg.customerName}!` : 'Thank you for your purchase!'} Your tutoring journey starts now.
        </p>
      </div>

      {/* Order summary card */}
      <div
        style={{
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0)' : 'translateY(20px)',
          transition: 'opacity 0.5s ease 0.25s, transform 0.5s ease 0.25s',
          width: '100%',
          maxWidth: 520,
          marginBottom: '2rem',
        }}
      >
        <div
          style={{
            background: 'linear-gradient(145deg, rgba(244,196,48,0.1) 0%, rgba(244,196,48,0.03) 100%)',
            border: '1.5px solid rgba(244,196,48,0.35)',
            borderRadius: 16,
            padding: '1.75rem 2rem',
          }}
        >
          <div
            style={{
              fontSize: '0.72rem',
              fontWeight: 700,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.35)',
              marginBottom: '1.25rem',
            }}
          >
            Order Summary
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
            <div>
              <div
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: '1.05rem',
                  fontWeight: 600,
                  color: '#fff',
                  marginBottom: '0.2rem',
                }}
              >
                {pkg.packageLabel} Tutoring Package
              </div>
              {pkg.packageHours > 0 && (
                <div style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.45)' }}>
                  {pkg.packageHours} {pkg.packageHours === 1 ? 'hour' : 'hours'} of one-on-one tutoring
                </div>
              )}
            </div>
            {pkg.packagePrice > 0 && (
              <div
                style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  color: '#f4c430',
                  flexShrink: 0,
                  marginLeft: '1rem',
                }}
              >
                ${pkg.packagePrice}
              </div>
            )}
          </div>

          <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.4)' }}>Order #:</span>
              <span
                style={{
                  fontSize: '0.85rem',
                  color: '#f4c430',
                  fontWeight: 600,
                  letterSpacing: '0.05em',
                }}
              >
                {orderNumber}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* What happens next */}
      <div
        style={{
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0)' : 'translateY(20px)',
          transition: 'opacity 0.5s ease 0.35s, transform 0.5s ease 0.35s',
          width: '100%',
          maxWidth: 520,
          marginBottom: '2.5rem',
        }}
      >
        <div
          style={{
            fontSize: '0.72rem',
            fontWeight: 700,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.35)',
            marginBottom: '1rem',
          }}
        >
          What happens next
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {steps.map((step, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '1rem',
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.07)',
                borderRadius: 12,
                padding: '1rem 1.25rem',
              }}
            >
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 10,
                  background: 'rgba(244,196,48,0.1)',
                  border: '1px solid rgba(244,196,48,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.2rem',
                  flexShrink: 0,
                }}
              >
                {step.icon}
              </div>
              <div>
                <div
                  style={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: '0.92rem',
                    fontWeight: 600,
                    color: '#fff',
                    marginBottom: '0.2rem',
                  }}
                >
                  {step.title}
                </div>
                <div style={{ fontSize: '0.83rem', color: 'rgba(255,255,255,0.5)', lineHeight: 1.5 }}>
                  {step.desc}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* CTA buttons */}
      <div
        style={{
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0)' : 'translateY(20px)',
          transition: 'opacity 0.5s ease 0.45s, transform 0.5s ease 0.45s',
          display: 'flex',
          gap: '0.75rem',
          flexWrap: 'wrap',
          justifyContent: 'center',
        }}
      >
        <button
          onClick={() => onNavigate('home')}
          style={{
            padding: '0.85rem 2rem',
            background: '#f4c430',
            border: 'none',
            borderRadius: 8,
            color: '#0a1628',
            fontSize: '0.92rem',
            fontWeight: 700,
            cursor: 'pointer',
            transition: 'opacity 0.2s',
          }}
          onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.opacity = '0.88')}
          onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.opacity = '1')}
        >
          Back to Home
        </button>
        <button
          onClick={() => onNavigate('tutors')}
          style={{
            padding: '0.85rem 2rem',
            background: 'transparent',
            border: '1px solid rgba(244,196,48,0.4)',
            borderRadius: 8,
            color: '#f4c430',
            fontSize: '0.92rem',
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'background 0.2s',
          }}
          onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.background = 'rgba(244,196,48,0.08)')}
          onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = 'transparent')}
        >
          Meet Our Tutors
        </button>
      </div>
    </section>
  );
};

export default OrderConfirmationPage;
